import { useEffect, useRef } from 'react';

const BRAND_COLORS = ['#014C8D', '#FFAE1E', '#1FA9C3', '#00205C', '#FFFFFF'];
const PARTICLE_COUNT = 90;
const DURATION_MS = 2500;
const FADE_START_MS = 2000;
const GRAVITY = 0.14;

/**
 * Full-screen canvas confetti burst. Mount when you want to celebrate;
 * unmount when you're done. Self-cleans its rAF loop on unmount and does
 * NOT intercept pointer events.
 */
export default function Confetti() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    const centerX = width / 2;
    const centerY = height / 2;

    const particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 6 + Math.random() * 9;
      particles.push({
        x: centerX + (Math.random() - 0.5) * 40,
        y: centerY + (Math.random() - 0.5) * 40,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 4, // slight upward bias for the burst
        size: 4 + Math.random() * 4,
        color: BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)],
        rotation: Math.random() * Math.PI * 2,
        angularVelocity: (Math.random() - 0.5) * 0.35,
      });
    }

    const start = performance.now();
    let rafId = 0;

    function tick(now) {
      const elapsed = now - start;
      if (elapsed >= DURATION_MS) {
        ctx.clearRect(0, 0, width, height);
        return;
      }

      const alpha =
        elapsed < FADE_START_MS
          ? 1
          : Math.max(0, 1 - (elapsed - FADE_START_MS) / (DURATION_MS - FADE_START_MS));

      ctx.clearRect(0, 0, width, height);
      ctx.globalAlpha = alpha;

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += GRAVITY;
        p.vx *= 0.99;
        p.rotation += p.angularVelocity;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        // Rectangle rather than circle — reads more like confetti paper.
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return <canvas ref={canvasRef} className="confetti-canvas" aria-hidden="true" />;
}
